import type { OnboardingData } from "@/pages/Onboarding";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth, useOrganization, useUser } from "@clerk/react";
import { toast } from "sonner";
import { useOrganizationSettingsStore } from "@/stores";
import {
  AlertCircleIcon,
  ArrowRightIcon,
  StarsIcon,
} from "lucide-react";
import React from "react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import posthog from "posthog-js";

interface CreateFaqProps {
  formData: OnboardingData;
  updateFormData: (data: Partial<OnboardingData>) => void;
  handleNext: () => void;
}

export default function CreateFaq({
  formData,
  updateFormData,
  handleNext,
}: CreateFaqProps) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const [error, setError] = React.useState<string | null>(null);
  const [faq, setFaq] = React.useState<string>(formData.faq_content || "");
  const {
    generateFaqFromWebsite,
    isGenerateFaqLoading,
    isLoading,
    saveOrganizationSetting,
  } = useOrganizationSettingsStore();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
    setFaq(value);
  };

  const handleGenerateFAQ = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = await getToken();
    if (!token) {
      toast.error("Failed to get authentication token");
      return;
    }
    if (user && organization) {
      const result = await generateFaqFromWebsite(
        organization.id,
        user.id,
        formData.website_url,
        formData.affiliate_page_link,
        token
      );
      if (result && result.faq_content) {
        updateFormData({ faq_content: result.faq_content });
        setFaq(result.faq_content);
        toast.success("FAQ generated successfully", {
          description: "You can edit the FAQ content in the area above.",
        });
        await saveOrganizationSetting(
          organization.id,
          user.id,
          "organization_faq",
          result.faq_content,
          token
        );
      } else {
        console.error("Failed to generate FAQ:", result?.error);
        if (result?.error) {
          setError(result.error);
        } else {
          // Fallback error message if no specific error is provided
          setError("Failed to generate FAQ content");
        }

        toast.error("Failed to generate FAQ.");
      }
    }

    posthog.capture("onboarding_faq_generated", {
      distinct_id: user?.id,
      organization_id: organization?.id,
      properties: {
        website_url: formData.website_url,
        affiliate_page_link: formData.affiliate_page_link,
      },
    });
  };

  const handleSubmit = async () => {
    const token = await getToken();
    if (!token) {
      toast.error("Failed to get authentication token");
      return;
    }
    if (user && organization) {
      await saveOrganizationSetting(
        organization.id,
        user.id,
        "organization_faq",
        faq,
        token
      );
      handleNext();
    }
  };

  return (
    <div className="max-w-2xl w-full min-h-full pb-20 flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between border bg-sidebar rounded-lg px-5 py-3 w-2xl">
        <span className="text-sm flex flex-col">
          <span className="text-lg">
            Generate your brand's FAQ for Amy using:
          </span>
          <span className="text-primary">
            {formData.website_url}, {formData.affiliate_page_link}
          </span>
        </span>
        <Button
          onClick={handleGenerateFAQ}
          disabled={!formData.website_url || isGenerateFaqLoading}
        >
          {!isGenerateFaqLoading && <StarsIcon />}
          {isGenerateFaqLoading && <Spinner />}
          {formData.faq_content ? "Regenerate..." : "Generate"}
        </Button>
      </div>

      {formData.faq_content && (
        <>
          <Textarea
            name="faq_content"
            value={faq}
            onChange={handleChange}
            placeholder="Enter your FAQ content here..."
            className="max-h-96"
            disabled={isGenerateFaqLoading}
          />

          <Button
            onClick={handleSubmit}
            className="w-full"
            disabled={isGenerateFaqLoading || !faq || isLoading}
          >
            Finish Up!
            {isLoading && <Spinner />}
            {!isLoading && (
              <ArrowRightIcon className="group-hover:translate-x-0.5 group-active:translate-x-1 transition-all" />
            )}
          </Button>
        </>
      )}

      {(error || (!formData.website_url && !formData.affiliate_page_link)) && (
        <>
          <Alert
            variant="destructive"
            className="bg-background dark:bg-transparent"
          >
            <AlertTitle>
              <AlertCircleIcon />
              {error ||
                "If you encounter any issues, please contact support at support@uplodio.com"}
            </AlertTitle>
          </Alert>
          <Button variant="ghost" onClick={handleNext} className="self-end">
            Skip
            <ArrowRightIcon className="group-hover:translate-x-0.5 group-active:translate-x-1 transition-all" />
          </Button>
        </>
      )}
    </div>
  );
}
